<template>
  <div data-cy="post-settlement-actions">
    <v-row no-gutters>
      <!-- ═══ Column 1: Finalize Invoice ═══ -->
      <v-col cols="12" sm="4" class="px-4 py-3" style="border-right: 1px solid #f0f0f0">
        <div class="font-weight-bold text-grey mb-1 d-flex align-center gap-1 text-caption" style="text-transform: uppercase; letter-spacing: 0.05em">
          1. Finalize Invoice
          <LlTooltip location="top">
            <div style="max-width: 260px">
              This invoice will be finalized immediately. The settlement wizard review
              replaces the standard invoice approval step. Billing period: All Time.
              Invoice date per firm settings.
            </div>
            <template #activator>
              <v-icon size="13" class="cursor-pointer text-grey">mdi-information-outline</v-icon>
            </template>
          </LlTooltip>
        </div>

        <p class="text-grey-darken-1 mb-2 text-body-2" style="font-style: italic">
          Finalized immediately — wizard review replaces approval.
        </p>

        <!-- Done: invoice exists -->
        <template v-if="invoiceFinalized">
          <div class="d-flex align-center gap-2 flex-wrap mb-1">
            <div class="d-flex align-center gap-1 text-body-2 font-weight-medium text-green-800">
              <LlIcon icon="mdi-check-circle" size="16" color="success" />
              Invoice Created
            </div>
            <a
              v-if="canUndoInvoice"
              class="text-decoration-underline cursor-pointer text-body-2 font-weight-medium text-error"
              data-cy="undo-invoice-link"
              @click="$emit('undo-invoice')"
            >
              Undo
            </a>
          </div>
          <div class="mb-1">
            <a
              class="text-decoration-underline cursor-pointer text-primary"
              data-cy="view-invoice-link"
              @click="$emit('view-invoice')"
            >
              View Invoice →
            </a>
          </div>
          <span v-if="!canUndoInvoice && invoiceBlockedReason" class="text-caption text-grey-darken-1" style="font-style: italic">
            {{ invoiceBlockedReason }}
          </span>
        </template>

        <!-- Not yet finalized -->
        <template v-else>
          <LlSecondaryButton
            color="primary"
            variant="elevated"
            size="small"
            :loading="saving && savingColumn === 'invoice'"
            :disabled="saving || !canFinalizeInvoice"
            data-cy="finalize-invoice-button"
            @click="$emit('finalize-invoice')"
          >
            {{ canUndoInvoice ? 'Retry Finalize' : 'Finalize Invoice' }}
          </LlSecondaryButton>
          <p v-if="!canFinalizeInvoice && invoiceBlockedReason" class="text-caption text-grey-darken-1 mt-1" style="font-style: italic">
            {{ invoiceBlockedReason }}
          </p>
          <!-- Draft exists from a failed submit: let the user inspect it, or remove it so the settlement can be reset. -->
          <template v-if="canUndoInvoice">
            <div class="mt-1">
              <a
                class="text-decoration-underline cursor-pointer text-primary"
                data-cy="view-invoice-link"
                @click="$emit('view-invoice')"
              >
                View Invoice →
              </a>
            </div>
            <div class="mt-1">
              <a
                class="text-decoration-underline cursor-pointer text-body-2 font-weight-medium text-error"
                data-cy="undo-draft-invoice-link"
                @click="$emit('undo-invoice')"
              >
                Undo draft invoice
              </a>
            </div>
          </template>
        </template>

        <div v-if="columnError === 'invoice'" class="text-caption mt-1 text-error" data-cy="invoice-error">
          {{ error }}
        </div>
      </v-col>

      <!-- ═══ Column 2: Pay from Trust ═══ -->
      <v-col cols="12" sm="4" class="px-4 py-3" style="border-right: 1px solid #f0f0f0">
        <div class="font-weight-bold text-grey mb-1 text-caption" style="text-transform: uppercase; letter-spacing: 0.05em">
          2. Pay from Trust
        </div>

        <!-- Trust balance + invoice amount info row -->
        <p class="text-grey-darken-1 mb-2 d-flex align-center flex-wrap text-body-2" style="gap: 4px">
          Trust:
          <span class="font-weight-medium">
            <template v-if="trustBalance !== null">{{ formatCurrency(trustBalance) }}</template>
            <template v-else-if="trustBalanceLoading">…</template>
            <template v-else>—</template>
          </span>
          <template v-if="invoiceAmount !== null">
            <span class="mx-1">|</span>
            Inv: <span class="font-weight-medium">{{ formatCurrency(invoiceAmount) }}</span>
          </template>
          <LlTooltip location="top">
            <div style="max-width: 300px">
              {{ trustScopeSentence }}
              Inv is the finalized invoice amount due.
              The system compares these amounts before allowing payment from trust.
              <br><br>
              Trust Available:
              <strong>
                <template v-if="trustBalance !== null">{{ formatCurrency(trustBalance) }}</template>
                <template v-else-if="trustBalanceLoading">Loading...</template>
                <template v-else>—</template>
              </strong>
              <br>
              Invoice Due:
              <strong>{{ invoiceAmount !== null ? formatCurrency(invoiceAmount) : '—' }}</strong>
            </div>
            <template #activator>
              <v-icon size="13" class="cursor-pointer text-grey">mdi-information-outline</v-icon>
            </template>
          </LlTooltip>
        </p>

        <!-- Done -->
        <template v-if="trustPaid || canUndoTrustPayment">
          <div class="d-flex align-center gap-2 flex-wrap mb-1">
            <div class="d-flex align-center gap-1 text-body-2 font-weight-medium text-green-800">
              <LlIcon icon="mdi-check-circle" size="16" color="success" />
              Paid from Trust
            </div>
            <a
              v-if="canUndoTrustPayment"
              class="text-decoration-underline cursor-pointer text-body-2 font-weight-medium text-error"
              data-cy="undo-trust-link"
              @click="$emit('undo-trust')"
            >
              Undo
            </a>
          </div>
          <span v-if="!canUndoTrustPayment" class="text-caption text-grey-darken-1" style="font-style: italic">
            {{ trustPaymentBlockedReason || undoTrustPaymentBlockedReason || 'Undo is not available right now.' }}
          </span>
        </template>

        <!-- Locked: invoice not finalized -->
        <template v-else-if="!invoiceFinalized">
          <LlSecondaryButton color="primary" variant="tonal" size="small" disabled data-cy="pay-trust-button-locked">
            Pay {{ invoiceAmount !== null ? formatCurrency(invoiceAmount) : '' }}
          </LlSecondaryButton>
          <p class="text-caption text-grey-darken-1 mt-1" style="font-style: italic">Finalize the invoice first.</p>
        </template>

        <!-- Locked by backend permission -->
        <template v-else-if="!canPayFromTrust && !trustPaid">
          <LlSecondaryButton color="primary" variant="tonal" size="small" disabled data-cy="pay-trust-button-blocked">
            Pay {{ invoiceAmount !== null ? formatCurrency(invoiceAmount) : '' }}
          </LlSecondaryButton>
          <p v-if="trustPaymentBlockedReason || trustInsufficient" class="text-caption mt-1 italic" :class="trustPaymentBlockedReason ? 'text-gray-500' : 'text-orange-700'">
            {{ trustPaymentBlockedReason || `Trust balance: ${trustBalance !== null ? formatCurrency(trustBalance) : '—'} / Required: ${invoiceAmount !== null ? formatCurrency(invoiceAmount) : '—'}` }}
          </p>
        </template>

        <!-- Ready -->
        <template v-else>
          <LlSecondaryButton
            color="primary"
            variant="elevated"
            size="small"
            :loading="saving && savingColumn === 'trust'"
            :disabled="saving"
            data-cy="pay-trust-button"
            @click="$emit('pay-trust')"
          >
            Pay {{ invoiceAmount !== null ? formatCurrency(invoiceAmount) : '' }}
          </LlSecondaryButton>
        </template>

        <div v-if="columnError === 'trust'" class="text-caption mt-1 text-error" data-cy="trust-error">
          {{ error }}
        </div>
      </v-col>

      <!-- ═══ Column 3: Client Disbursement ═══ -->
      <v-col cols="12" sm="4" class="px-4 py-3">
        <div class="font-weight-bold text-grey mb-1 text-caption" style="text-transform: uppercase; letter-spacing: 0.05em">
          3. Client Disbursement
        </div>

        <p v-if="netToClient !== null" class="text-grey-darken-1 mb-2 text-body-2">
          Net to Client: <span class="font-weight-medium">{{ formatCurrency(netToClient) }}</span>
        </p>

        <!-- Done -->
        <template v-if="qboPushed">
          <div class="d-flex align-center gap-2 flex-wrap mb-1">
            <div class="d-flex align-center gap-1 text-body-2 font-weight-medium text-green-800">
              <LlIcon icon="mdi-check-circle" size="16" color="success" />
              Pushed to QuickBooks
            </div>
            <a
              v-if="canUndoClientDisbursement"
              class="text-decoration-underline cursor-pointer text-body-2 font-weight-medium text-error"
              data-cy="undo-qbo-link"
              @click="$emit('undo-qbo')"
            >
              Undo
            </a>
          </div>
          <span v-if="!canUndoClientDisbursement" class="text-caption text-grey-darken-1" style="font-style: italic">
            {{ undoDisbursementBlockedReason || 'QuickBooks entry cannot be undone right now.' }}
          </span>
        </template>

        <!-- Locked: backend says push is not allowed -->
        <template v-else-if="!canPushClientDisbursement">
          <LlSecondaryButton color="primary" variant="tonal" size="small" disabled data-cy="push-qbo-button-locked">
            Submit to QuickBooks {{ netToClient !== null ? formatCurrency(netToClient) : '' }}
          </LlSecondaryButton>
          <p class="text-caption text-grey-darken-1 mt-1" style="font-style: italic">
            {{ disbursementBlockedReason || invoiceBlockedReason || (trustPaid ? 'Client disbursement cannot be pushed right now.' : 'Pay the invoice first.') }}
          </p>
        </template>

        <!-- Ready -->
        <template v-else>
          <LlSecondaryButton
            color="primary"
            variant="elevated"
            size="small"
            :loading="saving && savingColumn === 'qbo'"
            :disabled="saving"
            data-cy="push-qbo-button"
            @click="$emit('push-qbo')"
          >
            Submit to QuickBooks {{ netToClient !== null ? formatCurrency(netToClient) : '' }}
          </LlSecondaryButton>
        </template>

        <div v-if="columnError === 'qbo'" class="text-caption mt-1 text-error" data-cy="qbo-error">
          {{ error }}
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import { formatCurrency } from '@/services/utils';

const props = defineProps({
  /** True when settlementInvoiceId is non-null (invoice exists). */
  invoiceFinalized: { type: Boolean, default: false },
  /** True when the invoice can be finalized now (server-provided). */
  canFinalizeInvoice: { type: Boolean, default: false },
  /** True when the invoice can be undone now (server-provided). */
  canUndoInvoice: { type: Boolean, default: false },
  /** Reason finalize/undo is blocked (server-provided). */
  invoiceBlockedReason: { type: String, default: null },
  trustPaid: { type: Boolean, default: false },
  canPayFromTrust: { type: Boolean, default: false },
  canUndoTrustPayment: { type: Boolean, default: false },
  trustPaymentBlockedReason: { type: String, default: null },
  /** Reason trust-payment Undo is blocked (server-provided) — distinct from the push-blocked reason above. */
  undoTrustPaymentBlockedReason: { type: String, default: null },
  qboPushed: { type: Boolean, default: false },
  canPushClientDisbursement: { type: Boolean, default: false },
  canUndoClientDisbursement: { type: Boolean, default: false },
  disbursementBlockedReason: { type: String, default: null },
  /** Reason client-disbursement Undo is blocked (server-provided), e.g. QBO Cleared/Reconciled. */
  undoDisbursementBlockedReason: { type: String, default: null },
  invoiceAmount: { type: Number, default: null },
  netToClient: { type: Number, default: null },
  trustBalance: { type: Number, default: null },
  trustBalanceLoading: { type: Boolean, default: false },
  trustBalanceError: { type: Boolean, default: false },
  trustInsufficient: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  savingColumn: { type: String, default: null },
  error: { type: String, default: null },
  /** 'invoice' | 'trust' | 'qbo' | null — which column shows the inline error */
  columnError: { type: String, default: null },
  /** When set, shows a stage badge in the header (e.g. 'Settlement Pending'). */
  stageBadge: { type: String, default: null },
  /** True when trust statement is being downloaded (separate from action buttons). */
  trustStatementLoading: { type: Boolean, default: false },
  /** `settlementClientAccounting` flipper: on, the tooltip drops its matter-only wording. */
  clientAccountingEnabled: { type: Boolean, default: false },
});

// Drop the branch, keeping the neutral string, when the flipper goes.
const trustScopeSentence = computed(() =>
  props.clientAccountingEnabled
    ? 'Trust is the available trust balance for this settlement.'
    : 'Trust is the available client trust balance for this matter.',
);

defineEmits([
  'finalize-invoice',
  'undo-invoice',
  'pay-trust',
  'undo-trust',
  'push-qbo',
  'undo-qbo',
  'view-invoice',
]);
</script>
